import {LOCALE_KEY} from '../services/user-service/user-token.service';
import {TranslationDirective} from '../app/shared-module/directives/translation.directive';

export interface TimePeriodType {
  Name: 'WEEK' | 'TWO_WEEK' | 'MONTH' | 'SIX_MONTHS';
  Id: number;
  Description: string;
  Translation?: string;
}

export class DateTimeHelper {

  static readonly datePeriodTypes: TimePeriodType[] = [
    {
      Name: 'WEEK',
      Id: 1,
      Description: 'In einer Woche',
      Translation: 'ACTION_GENERAL_HEADLINE_PERIOD_WEEK',
    },
    {
      Name: 'TWO_WEEK',
      Id: 2,
      Description: 'In zwei Wochen',
      Translation: 'ACTION_GENERAL_HEADLINE_PERIOD_TWO_WEEKS',
    },
    {
      Name: 'MONTH',
      Id: 3,
      Description: 'In einem Monat',
      Translation: 'ACTION_GENERAL_HEADLINE_PERIOD_MONTH',
    },
    {
      Name: 'SIX_MONTHS',
      Id: 4,
      Description: 'In einem halben Jahr',
      Translation: 'ACTION_GENERAL_HEADLINE_PERIOD_HALF_YEAR',
    }
  ];

  static readonly divisions: {amount: number, name: Intl.RelativeTimeFormatUnit}[] = [
    { amount: 60, name: "seconds" },
    { amount: 60, name: "minutes" },
    { amount: 24, name: "hours" },
    { amount: 7, name: "days" },
    { amount: 4.34524, name: "weeks" },
    { amount: 12, name: "months" },
    { amount: Number.POSITIVE_INFINITY, name: "years" },
  ]

  static parseDate(time?: string): Date {
    if (typeof time === 'string' && time.match(/\d{4}-\d{2}-\d{2}T/) && !time.endsWith('Z')) {
      time += '+0200';
    }

    return time ? new Date(time) : new Date();
  }

  static getFormattedDateOnly(time?: string): string {
    const locale = sessionStorage.getItem(LOCALE_KEY) ?? 'de-DE';

    const date = DateTimeHelper.parseDate(time);
    const day = `0${date.getDate()}`.slice(-2);
    const month = `0${date.getMonth() + 1}`.slice(-2);

    return `${date.toLocaleString(locale, {weekday: 'short'})}, ${day}.${month}.${date.getFullYear()}`;
  }

  static getFormattedTimeOnly(time?: string): string {
    const date = DateTimeHelper.parseDate(time);
    const hours = `0${date.getHours()}`.slice(-2);
    const minutes = `0${date.getMinutes()}`.slice(-2);

    return `${hours}:${minutes}`;
  }

  static getFormattedDate(time?: string, customHours?: number, customMinutes?: number): string {
    const locale = sessionStorage.getItem(LOCALE_KEY) ?? 'de-DE';

    const date = DateTimeHelper.parseDate(time);
    const day = `0${date.getDate()}`.slice(-2);
    const month = `0${date.getMonth() + 1}`.slice(-2);
    const hours = `0${customHours !== undefined ? customHours : date.getHours()}`.slice(-2);
    const minutes = `0${customMinutes !== undefined ? customMinutes : date.getMinutes()}`.slice(-2);

    if (locale === 'it') {
      return `${date.toLocaleString(locale, {weekday: 'short'})}, ${day}.${month}.${date.getFullYear()} - ${TranslationDirective.data?.REQUEST_DETAIL_HISTORY_TIME_FROM_BEFORE_TIME_1} ${hours}:${minutes}`;
    }
    return `${date.toLocaleString(locale, {weekday: 'short'})}, ${day}.${month}.${date.getFullYear()} - ${hours}:${minutes}`;
  }

  static getHistoryFormattedDate(time: string): string {
    const locale = sessionStorage.getItem(LOCALE_KEY) ?? 'de-DE';

    const date = new Date(time);
    return `${date.toLocaleString(locale, {weekday: 'long'})},` +
      ` ${date.getDate()}. ${date.toLocaleString(locale, {month: 'long'})}` +
      ` ${date.getFullYear()} ${date.getHours()}:${date.getMinutes()}`;
  }

  static getDateAfterPeriod(afterPeriod: TimePeriodType): Date {
    let daysToAdd = 0;

    switch (afterPeriod.Name) {
      case 'WEEK':
        daysToAdd = 7;
        break;
      case 'TWO_WEEK':
        daysToAdd = 14;
        break;
      case 'MONTH':
        daysToAdd = 30;
        break;
      case 'SIX_MONTHS':
        daysToAdd = 183;
        break;
    }

    const date = new Date();
    date.setDate(date.getDate() + daysToAdd);
    date.setHours(12, 0, 0, 0);

    return date;
  }

  static getWeekDayName(index: number, lang: string = 'en', mode: 'short' | 'long' | 'narrow' = 'short'): string {
    const conditionalDate = new Date(2020, 2, 9 + index);
    return conditionalDate.toLocaleString(lang, { weekday: mode }).replace('.', '');
  }

  static getMonthName(index: number, lang: string = 'en', mode: 'short' | 'long' | 'narrow' = 'short'): string {
    const conditionalDate = new Date(2020, index, 1);
    return conditionalDate.toLocaleString(lang, { month: mode }).replace('.', '');
  }

  static getRelativeTime(date: string | Date, lang: string = 'en') {
    if (!date) {
      return '';
    }

    let duration = (new Date(date).valueOf() - new Date().valueOf()) / 1000;

    const formatter = new Intl.RelativeTimeFormat(lang ?? undefined, {
      numeric: "auto",
    });

    for (const division of this.divisions) {
      if (Math.abs(duration) < division.amount) {
        return formatter.format(Math.round(duration), division.name)
      }

      duration /= division.amount
    }

    return '';
  }
}
